import type { Provider as ProvisionProvider } from '@newfront-insurance/react-provision';
import { useProvider } from '@newfront-insurance/react-provision';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { NovaHostBridge, type NovaHostBridgeFatalReason, type NovaHostBridgeTelemetry } from './bridge';
import { IframeHost } from './iframe-host';
import type { HostApp, NovaHostContextPatch, NovaLauncherSize, NovaStateMessage } from './protocol';

interface NovaLoaderAuthContext {
  userDetails?: { uuid?: string };
}

export interface NovaChatOverlayProps<TAuthContext extends NovaLoaderAuthContext = NovaLoaderAuthContext> {
  authProvider: ProvisionProvider<TAuthContext>;
  hostApp: HostApp;
  transparentFloatingButton?: boolean;
  initialAccountUuid?: string;
  contextPatch?: NovaHostContextPatch;
  embedPath?: string;
  onFatalError?(reason: NovaHostBridgeFatalReason | 'killswitch'): void;
  onTelemetry?(event: NovaHostBridgeTelemetry): void;
}

const EMPTY_STATE: NovaStateMessage['payload'] = {
  enabled: false,
  open: false,
  dimension: 'panel',
  hasNotifications: false,
};
const EMPTY_CONTEXT_PATCH: NovaHostContextPatch = {};

export function NovaChatOverlay<TAuthContext extends NovaLoaderAuthContext>({
  authProvider,
  hostApp,
  transparentFloatingButton,
  initialAccountUuid,
  contextPatch = EMPTY_CONTEXT_PATCH,
  embedPath,
  onFatalError,
  onTelemetry,
}: NovaChatOverlayProps<TAuthContext>) {
  const { userDetails } = useProvider(authProvider);
  const router = useRouter();
  const ldClient = useLDClient();
  const [embedApps, setEmbedApps] = useState<HostApp[]>(
    () => (ldClient?.variation('nova-chat-embed-apps', []) as HostApp[] | undefined) ?? [],
  );
  const [state, setState] = useState(EMPTY_STATE);
  const [failed, setFailed] = useState(false);
  const [launcherRight, setLauncherRight] = useState(36);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const launcherRightRef = useRef(launcherRight);
  const launcherSizeRef = useRef<NovaLauncherSize>({ width: 184, height: 40 });
  const previousOpenRef = useRef(false);
  // This component only mounts after the host has opted into the embed path.
  const previousKillSwitchRef = useRef(true);
  const iframeId = `nova-assist-iframe-${hostApp}`;
  const launcherStorageKey = `nova-button-position-${hostApp}`;

  useEffect(() => {
    const stored = Number.parseInt(window.localStorage.getItem(launcherStorageKey) ?? '', 10);
    if (!Number.isFinite(stored)) return undefined;
    const frame = window.requestAnimationFrame(() => {
      launcherRightRef.current = stored;
      setLauncherRight(stored);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [launcherStorageKey]);

  useEffect(() => {
    if (state.launcher) launcherSizeRef.current = state.launcher;
  }, [state.launcher]);

  const clampLauncherRight = useCallback(
    (right: number) => Math.max(0, Math.min(right, window.innerWidth - launcherSizeRef.current.width)),
    [],
  );
  const handleLauncherMove = useCallback(
    ({ deltaX, persist }: { deltaX: number; persist: boolean }) => {
      const next = clampLauncherRight(launcherRightRef.current + deltaX);
      launcherRightRef.current = next;
      setLauncherRight(next);
      if (persist) window.localStorage.setItem(launcherStorageKey, String(next));
    },
    [clampLauncherRight, launcherStorageKey],
  );

  useEffect(() => {
    let frame = 0;
    const clamp = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const next = clampLauncherRight(launcherRightRef.current);
        if (next === launcherRightRef.current) return;
        launcherRightRef.current = next;
        setLauncherRight(next);
        window.localStorage.setItem(launcherStorageKey, String(next));
      });
    };
    clamp();
    window.addEventListener('resize', clamp);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', clamp);
    };
  }, [clampLauncherRight, launcherStorageKey, state.launcher]);

  const bridge = useMemo(
    () =>
      new NovaHostBridge({
        hostApp,
        requiredCapabilities: ['remote-launcher'],
        onState: setState,
        onFatalError: (reason) => {
          setFailed(true);
          onFatalError?.(reason);
        },
        onTelemetry,
      }),
    [hostApp, onFatalError, onTelemetry],
  );

  useLayoutEffect(() => {
    bridge.setLauncherMoveHandler(handleLauncherMove);
    bridge.start();
    return () => {
      bridge.setLauncherMoveHandler(undefined);
      bridge.stop();
    };
  }, [bridge, handleLauncherMove]);

  useEffect(() => {
    if (!ldClient) {
      return undefined;
    }
    const evaluate = () => setEmbedApps(ldClient.variation('nova-chat-embed-apps', []) as HostApp[]);
    evaluate();
    ldClient.on('change:nova-chat-embed-apps', evaluate);
    return () => ldClient.off('change:nova-chat-embed-apps', evaluate);
  }, [ldClient]);

  const killSwitchEnabled = embedApps.includes(hostApp);
  useEffect(() => {
    if (previousKillSwitchRef.current && !killSwitchEnabled) {
      onTelemetry?.({ name: 'killswitch_off', data: { hostApp } });
      bridge.attachIframe(null);
      onFatalError?.('killswitch');
    }
    previousKillSwitchRef.current = killSwitchEnabled;
  }, [bridge, hostApp, killSwitchEnabled, onFatalError, onTelemetry]);

  const queryAccountUuid = normalizeQueryValue(router.query.accountUuid);
  const pageAccountUuid =
    'accountUuid' in contextPatch
      ? pickValidUuid(contextPatch.accountUuid ?? undefined)
      : (pickValidUuid(initialAccountUuid) ?? pickValidUuid(queryAccountUuid));
  useEffect(() => {
    bridge.setContextPatch({ ...contextPatch, accountUuid: pageAccountUuid ?? null });
  }, [bridge, contextPatch, pageAccountUuid]);

  useEffect(() => {
    const updateVisibility = () => {
      bridge.setVisibility({ visible: document.visibilityState === 'visible', focused: document.hasFocus() });
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'KeyK' && (event.metaKey || event.ctrlKey) && state.enabled) {
        event.preventDefault();
        bridge.sendToggle();
      } else if (event.key === 'Escape' && state.open && state.dimension === 'panel') {
        bridge.sendClose();
      }
    };
    updateVisibility();
    document.addEventListener('visibilitychange', updateVisibility);
    window.addEventListener('focus', updateVisibility);
    window.addEventListener('blur', updateVisibility);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('visibilitychange', updateVisibility);
      window.removeEventListener('focus', updateVisibility);
      window.removeEventListener('blur', updateVisibility);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [bridge, state.dimension, state.enabled, state.open]);

  useEffect(() => {
    if (state.open && !previousOpenRef.current && bridge.isHandshakeComplete()) iframeRef.current?.focus();
    previousOpenRef.current = state.open;
  }, [bridge, state.open]);

  const userIsAuthenticated = Boolean(userDetails?.uuid);
  const renderChrome = userIsAuthenticated && killSwitchEnabled && !failed;

  if (!renderChrome) return null;

  return (
    <IframeHost
      iframeId={iframeId}
      iframeRef={iframeRef}
      embedPath={embedPath}
      dimension={state.dimension}
      open={state.enabled && state.open}
      launcher={state.enabled ? state.launcher : undefined}
      launcherRight={launcherRight}
      transparentLauncher={transparentFloatingButton}
      enabled={killSwitchEnabled}
      onMount={(iframe) => bridge.attachIframe(iframe)}
      onLoad={() => bridge.notifyIframeLoaded()}
    />
  );
}

function normalizeQueryValue(value: string | string[] | undefined): string | undefined {
  return Array.isArray(value) ? value[0] : value;
}

function pickValidUuid(value: string | undefined): string | undefined {
  // Keep this aligned with nova-core's account-bootstrap isUuid predicate.
  return value && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(value) ? value : undefined;
}
